<script lang="ts">
	import { onMount } from "svelte";
	import DisplayVariantManager from "./variantManager/variantManager.ec";
	import { createVariantsForStores, currentItemId, filtersStore, currentItem, variantsStore, currentFiltersParams, config } from "./scripts/stores";
	import { VariantPicker } from "./scripts/variantPicker";
	import { getSubitemsData, msg } from "./scripts/getters";
	import type Filter from "./models/Filter";
	import { isNullish } from "../generic/scripts/functions";
	import Subitems from "./views/Subitems.svelte";
	import Parameters from "./views/Parameters.svelte";
	import { Loader } from "../generic/components";

	export let currentId: number;
	export let articleId: number;
	/** Handles the display of the variants and DOM manipulation outside of the module. */
	let display: DisplayVariantManager;
	/** Handles the picking of the variant to display. */
	let picker: VariantPicker;
	/** The invalid parameters taken from the URL. */
	let invalidParams: [string, string][] = [];

	let errorMessage: string | null = null;

	onMount(() => {
		getSubitemsData(articleId, $config).then((data) => {
			if (data == null) {
				EC.debugger.error("ItemVariants :: onMount: data is null.");
				errorMessage = msg("error.fetchingVariants");
				return;
			}
			if (data.length === 0) {
				errorMessage = msg("error.noVariants");
				return;
			}
			display = new DisplayVariantManager($config);
			picker = new VariantPicker($config);
			createVariantsForStores(data);
			const { variantId, missingParams, illegalParams } = picker.pickVariant(currentId);
			if ($config.SHOW_INVALID_VARIANT) {
				invalidParams = [...(missingParams ?? []), ...(illegalParams ?? [])];
			}
			const subitemData = $variantsStore.get(variantId)?.data;
			if (subitemData == null) {
				errorMessage = msg("error.fetchingVariantData");
				return;
			}
			currentItemId.set(variantId);
			display.variantChanged(subitemData, { params: getParams() });
			if (errorMessage != null) {
				errorMessage = null;
			}
		});
	});

	function onParametersChange(event: CustomEvent<{ filter: Filter; value: string }>) {
		const { filter, value } = event.detail;
		const newFilterValue = { id: filter.id, symbol: filter.symbol, value };
		if ($currentItem?.hasFilterValue(newFilterValue)) {
			return;
		}
		onVariantChange(picker.findVariantToDisplay(newFilterValue));
	}

	function onVariantChange(newId: number) {
		invalidParams = [];
		const subitemData = $variantsStore.get(newId)?.data;
		if (subitemData == null) {
			errorMessage = msg("error.fetchingVariantData");
			return;
		}
		currentItemId.set(newId);
		display.variantChanged(subitemData, { params: getParams() });
		if (errorMessage != null) {
			errorMessage = null;
		}
	}

	function getParams() {
		const shouldAddParams = $config.ADD_PARAMS_TO_URL;
		let urlParams: Map<string, string> | undefined = undefined;
		if (shouldAddParams && !isNullish($currentItem)) {
			if ($filtersStore.size > 0) {
				urlParams = $currentFiltersParams;
			} else if ($variantsStore.size > 0) {
				urlParams = new Map([[$config.VARIANT_PARAM_NAME, $currentItem.data.symbol]]);
			}
		}
		return { filters: $currentFiltersParams, invalid: invalidParams, url: urlParams };
	}
</script>

<div class="position-relative">
	{#if errorMessage != null && errorMessage.length > 0}
		<div class="alert alert-danger my-3" role="alert">
			{errorMessage}
		</div>
	{:else if !isNullish($filtersStore) && !isNullish($variantsStore) && !isNullish($currentItem)}
		{@const hasFilters = $filtersStore.size > 0}
		{@const hasItems = $variantsStore.size > 0}
		{@const SHOW_PARAM_SELECT = $config.SHOW_PARAM_SELECT}
		{#if hasFilters || hasItems}
			<div class="my-3">
				{#if SHOW_PARAM_SELECT && hasFilters && hasItems}
					<Parameters {invalidParams} on:change={onParametersChange} />
				{:else if hasItems}
					<Subitems {invalidParams} on:change={(event) => onVariantChange(event.detail)} />
				{/if}
			</div>
		{/if}
	{:else}
		<Loader outDelay={300} />
	{/if}
</div>
